import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkAwsLaunch } from '../store/actions/cluster.actions';

const DEBOUNCE_MS = 800;

export const launchCheckKey = ({ credentialId, region, imageId, instanceType, zoneName }) => (
    `${credentialId}|${region}|${imageId}|${instanceType}|${zoneName || ''}`
);

/**
 * AWS's own verdict on each complete (image, instance type, zone) the form
 * has chosen, via run-instances --dry-run on the API. Results live in
 * Redux keyed by the exact combination, so a slow answer for a selection
 * the user already left can never overwrite the current one.
 *
 *   requests: [{ role, imageId, instanceType, zoneName }]  (incomplete ones are ignored)
 * Returns { byRole: { [role]: { loading, ok, skipped, code, message } }, rejected: [{ role, message }], checking }
 */
export default function useLaunchCheck({ credentialId, region, requests, enabled = true }) {
    const dispatch = useDispatch();
    const launchChecks = useSelector(({ clusters }) => clusters.cluster.awsCatalog.launchChecks) || {};
    const timers = useRef({});

    const complete = useMemo(() => (
        (requests || []).filter((r) => r && r.imageId && r.instanceType).map((r) => ({
            ...r, key: launchCheckKey({ credentialId, region, imageId: r.imageId, instanceType: r.instanceType, zoneName: r.zoneName }),
        }))
    ), [requests, credentialId, region]);

    useEffect(() => {
        if (!enabled || !credentialId || !region) return undefined;
        complete.forEach((r) => {
            if (launchChecks[r.key] || timers.current[r.key]) return;
            timers.current[r.key] = setTimeout(() => {
                delete timers.current[r.key];
                dispatch(checkAwsLaunch({ credentialId, region, imageId: r.imageId, instanceType: r.instanceType, zoneName: r.zoneName || null }));
            }, DEBOUNCE_MS);
        });
        return undefined;
    }, [dispatch, enabled, credentialId, region, complete, launchChecks]);

    useEffect(() => () => {
        Object.values(timers.current).forEach(clearTimeout);
        timers.current = {};
    }, []);

    return useMemo(() => {
        const byRole = {};
        const rejected = [];
        let checking = false;
        complete.forEach((r) => {
            const v = launchChecks[r.key];
            if (!v) { byRole[r.role] = { loading: true }; checking = true; return; }
            byRole[r.role] = v;
            if (v.loading) checking = true;
            if (v.ok === false) rejected.push({ role: r.role, code: v.code, message: v.message });
        });
        return { byRole, rejected, checking };
    }, [complete, launchChecks]);
}
